// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import clsx from 'clsx'
import styles from './BandChips.module.css'
import {
  bandLabel,
  bandRangeText,
  bandSentence,
  dbText,
  VERDICT_DB,
  type BandPair,
  type BandVerdict,
  type CurveKind,
} from 'lib/owner-notes/deltas'

interface BandChipsProps {
  /** Every band's mean difference for the visible measure, in band order. */
  verdicts: readonly BandVerdict[]
  kind: CurveKind
  /** The band the chart is currently calling out; null (the default) calls out none. */
  selected: BandVerdict | null
  /** A band's name, or null when the reader clicks the selected chip again to clear it. */
  onSelect: (band: string | null) => void
  /** Who the sentences name; "this one" / "yours" when absent. */
  pair?: BandPair
}

/**
 * Bass / mids / treble as chips under a lab chart: each says its range and what this product
 * does there against the reader's, and picking one moves the chart's callout to it; picking it
 * again clears the callout. Nothing is picked until the reader picks (2026-10-23). Extracted
 * from `LabCompareDialog` unchanged (2026-09-15) so the dialog and the guide's inline panel say
 * the same thing the same way — the wording all derives from one `BandVerdict` (8ec578e0).
 */
const BandChips: React.VFC<BandChipsProps> = ({ verdicts, kind, selected, onSelect, pair }) => (
  <div className={styles.bands} role="group" aria-label="Difference by band">
    {verdicts.map(v => {
      const same = Math.abs(v.db) < VERDICT_DB
      return (
        <button
          key={v.band.name}
          type="button"
          className={clsx(styles.band, {
            [styles.bandOn]: selected === v,
            [styles.bandSame]: same,
          })}
          aria-pressed={selected === v}
          onClick={() => onSelect(selected === v ? null : v.band.name)}
        >
          <span className={styles.bandHead}>
            <span className={styles.bandName}>{bandLabel(v.band)}</span>
            <span className={styles.bandRange}>{bandRangeText(v.band)}</span>
            {v.mixed && <span className={styles.bandTag}>{'mixed'}</span>}
          </span>
          <span className={styles.bandSentence}>
            {same ? (
              <>
                <span className={styles.bandDb}>{dbText(v.db)}</span>
                {` · about the same as ${pair?.that ?? 'yours'}`}
              </>
            ) : (
              bandSentence(v, kind, pair)
            )}
          </span>
        </button>
      )
    })}
  </div>
)

export default BandChips
